/**
 * Tipos TypeScript para o módulo de Contratos e Assinatura Digital
 * 
 * RELEASE: v1.0.0 - 2026-02-16
 * Descrição: Tipos para templates, contratos, signatários e providers
 */

/**
 * Status possíveis de um contrato
 */
export type ContractStatus =
  | 'draft'
  | 'generated'
  | 'sent'
  | 'signed'
  | 'expired'
  | 'cancelled'
  | 'queued_pdf'
  | 'processing_pdf'
  | 'queued_signature'
  | 'processing_signature'
  | 'error';

/**
 * Status possíveis de uma assinatura individual
 */
export type SignatureStatus = 'pending' | 'signed' | 'refused' | 'expired';

/**
 * Provider de assinatura digital (Autentique, DocuSign, ZapSign)
 */
export interface SignatureProvider {
  id: string;
  name: string;
  display_name: string;
  slug: string;
  is_active: boolean;
  settings_schema: Record<string, unknown>;
  created_at: string;
}

/**
 * Configuração do provider de assinatura por tenant
 */
export interface TenantSignatureConfig {
  id: string;
  tenant_id: string;
  provider_id: string;
  credentials: Record<string, unknown>;
  is_default: boolean;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Join com signature_providers */
  signature_providers?: SignatureProvider;
}

/**
 * Variável disponível em um template de contrato
 */
export interface ContractVariable {
  /** Chave da variável (ex: nome_cliente) */
  key: string;
  /** Rótulo para exibição (ex: Nome do Cliente) */
  label: string;
  /** Descrição da variável */
  description?: string;
  /** Tipo da variável (text, currency, date, list) */
  type: 'text' | 'currency' | 'date' | 'list';
  /** Origem dos dados (contact, deal, tenant, custom) */
  source: 'contact' | 'deal' | 'tenant' | 'custom';
  /** Campo de mapeamento no objeto de origem */
  source_field?: string;
  /** Subgrupo opcional dentro de um source (ex: 'totals' dentro de deal) */
  group?: string;
}

/**
 * Modelo/Template de contrato
 */
export interface ContractTemplate {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  content_html: string;
  variables: ContractVariable[];
  category: string;
  is_active: boolean;
  version: number;
  /** Métodos de entrega permitidos para assinatura (email, whatsapp) */
  allowed_delivery_methods: ('email' | 'whatsapp')[];
  /** Se true, exige selfie com liveness check (LIVE) de cada signatário */
  require_selfie: boolean;
  /** Caminho da imagem de fundo no Storage (bucket contratos) */
  background_image_path: string | null;
  /** Margem superior em mm quando há plano de fundo (padrão: 33mm) */
  bg_margin_top_mm: number;
  /** Margem inferior em mm quando há plano de fundo (padrão: 20mm) */
  bg_margin_bottom_mm: number;
  /** Se ativa a paginação automática no PDF */
  show_page_numbers: boolean;
  /** Posição da paginação: 'top-right' ou 'bottom-right' */
  page_number_position: string;
  /** Offset vertical em mm da borda para posicionamento da paginação (padrão: 12mm) */
  pagination_y_offset_mm: number;
  /** URL do PDF base com cláusulas fixas (merge com anexo dinâmico) */
  base_pdf_path: string | null;
  /** Caminho do PDF de capa (primeira página do documento) */
  cover_pdf_path: string | null;
  /** Posição do PDF base: 'before' (antes do anexo) ou 'after' (depois) */
  base_pdf_position: string;
  /**
   * Tipo de origem do modelo:
   *  - 'html'          → editor HTML tradicional (padrão)
   *  - 'uploaded_pdf'  → PDF pronto enviado pelo usuário; wizard pula variáveis
   * @release v2026-05-XX
   */
  source_type: 'html' | 'uploaded_pdf';
  /** Caminho no Storage do PDF pronto (apenas quando source_type = 'uploaded_pdf') */
  source_pdf_storage_path: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Join opcional com contract_template_versions — trazido quando o select inclui o relacionamento.
   * Usado para calcular a versão real exibida na listagem (ex: v1.2 = version_number 3 - 1).
   */
  contract_template_versions?: { version_number: number }[];
}


/**
 * Input para criar/atualizar template
 */
export interface ContractTemplateInput {
  name: string;
  description?: string;
  content_html: string;
  variables?: ContractVariable[];
  category?: string;
  is_active?: boolean;
  /** Métodos de entrega permitidos */
  allowed_delivery_methods?: ('email' | 'whatsapp')[];
  /** Se true, exige selfie com liveness check do signatário */
  require_selfie?: boolean;
  /** Caminho da imagem de fundo no Storage */
  background_image_path?: string | null;
  /** Margem superior em mm quando há plano de fundo */
  bg_margin_top_mm?: number;
  /** Margem inferior em mm quando há plano de fundo */
  bg_margin_bottom_mm?: number;
  /** Se ativa a paginação automática no PDF */
  show_page_numbers?: boolean;
  /** Posição da paginação: 'top-right' ou 'bottom-right' */
  page_number_position?: string;
  /** Offset vertical em mm da borda para posicionamento da paginação */
  pagination_y_offset_mm?: number;
  /** URL do PDF base com cláusulas fixas */
  base_pdf_path?: string | null;
  /** Caminho do PDF de capa (primeira página do documento) */
  cover_pdf_path?: string | null;
  /** Posição do PDF base: 'before' (antes do anexo) ou 'after' (depois) */
  base_pdf_position?: string;
  /** Tipo de origem do modelo: 'html' (editor) ou 'uploaded_pdf' (PDF pronto) */
  source_type?: 'html' | 'uploaded_pdf';
  /** Caminho do PDF pronto no Storage (apenas quando source_type = 'uploaded_pdf') */
  source_pdf_storage_path?: string | null;
}


/**
 * Contrato gerado a partir de um template
 */
export interface Contract {
  id: string;
  tenant_id: string;
  deal_id: string | null;
  template_id: string | null;
  title: string;
  content_html: string;
  pdf_storage_path: string | null;
  status: ContractStatus;
  /** Número sequencial do contrato (8 dígitos, gerado automaticamente por tenant) */
  contract_number: number | null;
  variables_data: Record<string, string>;
  generated_at: string | null;
  sent_at: string | null;
  signed_at: string | null;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** ID da versão do template efetivamente usada no momento da geração (rastreabilidade) */
  used_template_version_id: string | null;
  /** ID do contrato publicado que originou a geração */
  published_contract_id: string | null;
  /** Joins */
  contract_templates?: ContractTemplate;
  contract_signatures?: ContractSignature[];
  /** Versão do template usada — retornada via join com contract_template_versions!used_template_version_id */
  contract_template_versions?: { version_number: number } | null;
}

/**
 * Signatário de um contrato
 */
export interface ContractSignature {
  id: string;
  contract_id: string;
  tenant_id: string;
  signer_name: string;
  signer_email: string | null;
  signer_phone: string | null;
  signer_role: string;
  sign_order: number;
  status: SignatureStatus;
  signed_at: string | null;
  provider_signer_id: string | null;
  provider_data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Tracking do contrato no provider externo
 */
export interface ContractProviderTracking {
  id: string;
  contract_id: string;
  tenant_id: string;
  provider_id: string;
  external_document_id: string | null;
  external_url: string | null;
  provider_status: string;
  provider_data: Record<string, unknown>;
  webhook_url: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Variáveis pré-definidas do sistema para mapeamento automático
 * Usadas para preencher automaticamente dados do deal/contato
 */
export const PREDEFINED_VARIABLES: ContractVariable[] = [
  { key: 'nome_cliente', label: 'Nome do Cliente', type: 'text', source: 'contact', source_field: 'name' },
  { key: 'email_cliente', label: 'E-mail do Cliente', type: 'text', source: 'contact', source_field: 'email' },
  { key: 'telefone_cliente', label: 'Telefone do Cliente', type: 'text', source: 'contact', source_field: 'phone' },
  { key: 'cpf_cnpj', label: 'CPF/CNPJ', type: 'text', source: 'contact', source_field: 'document' },
  { key: 'endereco_cliente', label: 'Endereço do Cliente', type: 'text', source: 'contact', source_field: 'address' },
  { key: 'data_nascimento', label: 'Data de Nascimento', type: 'date', source: 'contact', source_field: 'birth_date' },
  { key: 'endereco', label: 'Endereço', type: 'text', source: 'contact', source_field: 'address_full' },
  { key: 'cidade_estado', label: 'Cidade e Estado', type: 'text', source: 'contact', source_field: 'city_state' },
  { key: 'cep', label: 'CEP', type: 'text', source: 'contact', source_field: 'zip_code' },
  { key: 'titulo_negocio', label: 'Título do Negócio', type: 'text', source: 'deal', source_field: 'title' },
  { key: 'valor_negocio', label: 'Valor do Negócio', type: 'currency', source: 'deal', source_field: 'value' },
  { key: 'moeda', label: 'Moeda', type: 'text', source: 'deal', source_field: 'currency' },
  { key: 'data_hoje', label: 'Data Atual', type: 'date', source: 'custom' },
  { key: 'data_previsao', label: 'Data de Previsão', type: 'date', source: 'deal', source_field: 'expected_close_date' },
  { key: 'nome_vendedor', label: 'Nome do Vendedor', type: 'text', source: 'deal', source_field: 'seller_name' },
  { key: 'nome_empresa', label: 'Nome da Empresa', type: 'text', source: 'tenant', source_field: 'name' },
  { key: 'produtos_servicos', label: 'Produtos/Serviços', type: 'list', source: 'deal', source_field: 'items' },
  /** Variáveis de workspace e produto - adicionadas em v1.1.0 */
  { key: 'nome_workspace', label: 'Nome do Workspace', type: 'text', source: 'deal', source_field: 'workspace_name' },
  { key: 'categoria_produto', label: 'Categoria do Produto', type: 'text', source: 'deal', source_field: 'product_category' },
  { key: 'subcategoria_produto', label: 'Subcategoria do Produto', type: 'text', source: 'deal', source_field: 'product_subcategory' },
  { key: 'quantidade_produto', label: 'Quantidade', type: 'text', source: 'deal', source_field: 'product_quantity' },
  { key: 'valor_produto', label: 'Valor Unitário do Produto', type: 'currency', source: 'deal', source_field: 'product_unit_price' },
  { key: 'desconto', label: 'Desconto', type: 'currency', source: 'deal', source_field: 'discount' },
  { key: 'valor_total', label: 'Valor Total (Qtd x Unitário)', type: 'currency', source: 'deal', source_field: 'product_total' },
  { key: 'sku_produto', label: 'SKU (Código do Produto)', type: 'text', source: 'deal', source_field: 'product_sku' },
  /**
   * Totais agregados do negócio — espelham os cards da aba Produtos do modal de negócio.
   * Calculados em runtime no wizard a partir dos itens (crm_deal_items + crm_products).
   * @release v1.2.0 - 2026-05-19 — Suporte a serviços avulsos e recorrentes
   */
  { key: 'subtotal_negocio', label: 'Subtotal do Negócio', type: 'currency', source: 'deal', source_field: 'subtotal', group: 'totals' },
  { key: 'desconto_total', label: 'Desconto Total', type: 'currency', source: 'deal', source_field: 'discount_total', group: 'totals' },
  { key: 'total_negocio', label: 'Total do Negócio', type: 'currency', source: 'deal', source_field: 'total', group: 'totals' },
  { key: 'mrr_total', label: 'Total Mensal (MRR)', type: 'currency', source: 'deal', source_field: 'mrr', group: 'totals' },
  { key: 'total_vigencia_contrato', label: 'Total Vigência do Contrato', type: 'currency', source: 'deal', source_field: 'lifetime_value', group: 'totals' },
  { key: 'quantidade_itens', label: 'Qtd. de Itens', type: 'text', source: 'deal', source_field: 'item_count', group: 'totals' },
  { key: 'quantidade_produtos', label: 'Qtd. de Produtos', type: 'text', source: 'deal', source_field: 'product_count', group: 'totals' },
  { key: 'quantidade_servicos', label: 'Qtd. de Serviços', type: 'text', source: 'deal', source_field: 'service_count', group: 'totals' },
  { key: 'tem_servico_recorrente', label: 'Possui Serviço Recorrente', type: 'text', source: 'deal', source_field: 'has_recurring', group: 'totals' },
  /**
   * Veículos — preenchidas pelo wizard a partir dos veículos selecionados.
   * As variáveis simples referem-se ao PRIMEIRO veículo selecionado; para
   * múltiplos veículos utilize o loop {{#veiculos}}...{{/veiculos}}.
   * @release v1.3.0 - 2026-08-09 (Fase 7 do módulo de Veículos)
   */
  { key: 'veiculo_placa', label: 'Veículo — Placa', type: 'text', source: 'deal', source_field: 'vehicle_plate', group: 'vehicles' },
  { key: 'veiculo_tipo', label: 'Veículo — Tipo', type: 'text', source: 'deal', source_field: 'vehicle_type', group: 'vehicles' },
  { key: 'veiculo_marca', label: 'Veículo — Marca', type: 'text', source: 'deal', source_field: 'vehicle_brand', group: 'vehicles' },
  { key: 'veiculo_modelo', label: 'Veículo — Modelo', type: 'text', source: 'deal', source_field: 'vehicle_model', group: 'vehicles' },
  { key: 'veiculo_ano', label: 'Veículo — Ano', type: 'text', source: 'deal', source_field: 'vehicle_year', group: 'vehicles' },
  { key: 'veiculo_cor', label: 'Veículo — Cor', type: 'text', source: 'deal', source_field: 'vehicle_color', group: 'vehicles' },
  { key: 'veiculo_chassi', label: 'Veículo — Chassi', type: 'text', source: 'deal', source_field: 'vehicle_chassis', group: 'vehicles' },
  { key: 'veiculo_renavam', label: 'Veículo — Renavam', type: 'text', source: 'deal', source_field: 'vehicle_renavam', group: 'vehicles' },
  { key: 'veiculo_codigo_fipe', label: 'Veículo — Código FIPE', type: 'text', source: 'deal', source_field: 'vehicle_fipe_code', group: 'vehicles' },
  { key: 'veiculo_valor_fipe', label: 'Veículo — Valor FIPE', type: 'currency', source: 'deal', source_field: 'vehicle_fipe_value', group: 'vehicles' },
  { key: 'quantidade_veiculos', label: 'Qtd. de Veículos', type: 'text', source: 'deal', source_field: 'vehicle_count', group: 'vehicles' },
];

/**
 * Template HTML da tabela de veículos (loop {{#veiculos}}).
 * Renderiza uma linha por veículo selecionado no wizard.
 * @release v1.0.0 - 2026-08-09
 */
export const VEHICLES_TABLE_TEMPLATE = `<table style="width:100%;border-collapse:collapse;border:1px solid #ccc;">
  <thead>
    <tr style="background:#f5f5f5;">
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Placa</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Tipo</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Marca / Modelo</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Ano</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Cor</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Valor FIPE</th>
    </tr>
  </thead>
  <tbody>
    {{#veiculos}}
    <tr>
      <td style="border:1px solid #ccc;padding:6px;">{{veiculo_item_placa}}</td>
      <td style="border:1px solid #ccc;padding:6px;">{{veiculo_item_tipo}}</td>
      <td style="border:1px solid #ccc;padding:6px;">{{veiculo_item_marca}} {{veiculo_item_modelo}}</td>
      <td style="border:1px solid #ccc;padding:6px;">{{veiculo_item_ano}}</td>
      <td style="border:1px solid #ccc;padding:6px;">{{veiculo_item_cor}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{veiculo_item_valor_fipe}}</td>
    </tr>
    {{/veiculos}}
  </tbody>
</table>`;

/**
 * Variáveis internas do bloco de loop {{#produtos}}...{{/produtos}}
 * Usadas para gerar linhas dinâmicas de produtos no template
 * 
 * @release v1.2.0 - 2026-05-19 — Adicionado suporte a serviços (avulso/recorrente)
 */
export interface ProductLoopVariable {
  /** Chave interna do loop (ex: item_nome) */
  key: string;
  /** Rótulo para exibição */
  label: string;
  /** Tipo da variável */
  type: 'text' | 'currency';
}

export const PRODUCT_LOOP_VARIABLES: ProductLoopVariable[] = [
  { key: 'item_nome', label: 'Nome do Produto', type: 'text' },
  { key: 'item_sku', label: 'SKU', type: 'text' },
  { key: 'item_tipo', label: 'Tipo (Produto/Serviço)', type: 'text' },
  { key: 'item_unidade', label: 'Unidade', type: 'text' },
  { key: 'item_categoria', label: 'Categoria', type: 'text' },
  { key: 'item_subcategoria', label: 'Subcategoria', type: 'text' },
  { key: 'item_quantidade', label: 'Quantidade', type: 'text' },
  { key: 'item_valor_unitario', label: 'Valor Unitário', type: 'currency' },
  { key: 'item_mensalidade', label: 'Mensalidade', type: 'currency' },
  { key: 'item_periodicidade', label: 'Periodicidade', type: 'text' },
  { key: 'item_prazo', label: 'Prazo', type: 'text' },
  { key: 'item_prazo_quantidade', label: 'Qtd. do Prazo', type: 'text' },
  { key: 'item_prazo_unidade', label: 'Unidade do Prazo', type: 'text' },
  { key: 'item_desconto', label: 'Desconto', type: 'currency' },
  { key: 'item_desconto_percentual', label: 'Desconto (%)', type: 'text' },
  { key: 'item_desconto_fixo', label: 'Desconto (R$)', type: 'currency' },
  { key: 'item_total', label: 'Total', type: 'currency' },
  { key: 'item_total_vigencia', label: 'Total na Vigência', type: 'currency' },
];

/**
 * Representa um item de produto no wizard de contrato
 * Dados extraídos de crm_deal_items com joins
 *
 * @release v1.2.0 - 2026-05-19 — Campos opcionais para serviços (avulso/recorrente)
 */
export interface WizardDealItem {
  id: string;
  product_name: string;
  sku: string;
  category: string;
  subcategory: string;
  quantity: number;
  unit_price: number;
  discount: number;
  total: number;
  /** Campos novos para suporte a serviços. Opcionais para preservar compatibilidade. */
  product_type?: 'product' | 'service' | null;
  unit_type?: string | null;
  recurring_price?: number | null;
  recurrence_interval?: string | null;
  term_type?: string | null;
  term_quantity?: number | null;
  term_unit?: string | null;
  discount_percent?: number | null;
  discount_fixed?: number | null;
  /** Total estimado na vigência do contrato (para serviços recorrentes) */
  lifetime_total?: number | null;
}

/**
 * Template HTML da tabela de produtos para inserção no editor (apenas Produtos)
 */
export const PRODUCT_TABLE_TEMPLATE = `<table style="width:100%;border-collapse:collapse;border:1px solid #ccc;">
  <thead>
    <tr style="background:#f5f5f5;">
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Produto</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">SKU</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Qtd</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Valor Unit.</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Desconto</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Total</th>
    </tr>
  </thead>
  <tbody>
    {{#produtos}}
    <tr>
      <td style="border:1px solid #ccc;padding:6px;">{{item_nome}}</td>
      <td style="border:1px solid #ccc;padding:6px;">{{item_sku}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_quantidade}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_valor_unitario}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_desconto}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_total}}</td>
    </tr>
    {{/produtos}}
  </tbody>
</table>`;

/**
 * Template HTML com colunas focadas em SERVIÇOS (avulso/recorrente).
 * Exibe Mensalidade e Prazo. Itens não-recorrentes mostram coluna vazia.
 * @release v1.0.0 - 2026-05-19
 */
export const SERVICES_TABLE_TEMPLATE = `<table style="width:100%;border-collapse:collapse;border:1px solid #ccc;">
  <thead>
    <tr style="background:#f5f5f5;">
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Serviço</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Unidade</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Qtd</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Preço</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Mensalidade</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Prazo</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Desconto</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Total</th>
    </tr>
  </thead>
  <tbody>
    {{#produtos}}
    <tr>
      <td style="border:1px solid #ccc;padding:6px;">{{item_nome}}</td>
      <td style="border:1px solid #ccc;padding:6px;">{{item_unidade}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_quantidade}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_valor_unitario}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_mensalidade}}</td>
      <td style="border:1px solid #ccc;padding:6px;">{{item_prazo}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_desconto}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_total}}</td>
    </tr>
    {{/produtos}}
  </tbody>
</table>`;

/**
 * Tabela COMPLETA — espelha 100% a grid da aba Produtos do modal de negócio.
 * Cobre produtos e serviços (avulsos/recorrentes) em um único bloco.
 * @release v1.0.0 - 2026-05-19
 */
export const FULL_ITEMS_TABLE_TEMPLATE = `<table style="width:100%;border-collapse:collapse;border:1px solid #ccc;">
  <thead>
    <tr style="background:#f5f5f5;">
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Item</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Tipo</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Unidade</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Qtd</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Preço</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Mensalidade</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:left;">Prazo</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Desconto</th>
      <th style="border:1px solid #ccc;padding:6px;text-align:right;">Total</th>
    </tr>
  </thead>
  <tbody>
    {{#produtos}}
    <tr>
      <td style="border:1px solid #ccc;padding:6px;">{{item_nome}}</td>
      <td style="border:1px solid #ccc;padding:6px;">{{item_tipo}}</td>
      <td style="border:1px solid #ccc;padding:6px;">{{item_unidade}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_quantidade}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_valor_unitario}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_mensalidade}}</td>
      <td style="border:1px solid #ccc;padding:6px;">{{item_prazo}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_desconto}}</td>
      <td style="border:1px solid #ccc;padding:6px;text-align:right;">{{item_total}}</td>
    </tr>
    {{/produtos}}
  </tbody>
</table>`;

/**
 * Resumo de totais — bloco compacto para inserir no final do contrato.
 * Usa variáveis simples agregadas, calculadas pelo wizard a partir dos itens.
 * @release v1.0.0 - 2026-05-19
 */
export const TOTALS_SUMMARY_TEMPLATE = `<table style="width:100%;border-collapse:collapse;margin-top:12px;">
  <tbody>
    <tr><td style="padding:4px 8px;text-align:right;">Subtotal:</td><td style="padding:4px 8px;text-align:right;width:160px;"><strong>{{subtotal_negocio}}</strong></td></tr>
    <tr><td style="padding:4px 8px;text-align:right;">Descontos:</td><td style="padding:4px 8px;text-align:right;"><strong>{{desconto_total}}</strong></td></tr>
    <tr><td style="padding:4px 8px;text-align:right;border-top:1px solid #ccc;">Total:</td><td style="padding:4px 8px;text-align:right;border-top:1px solid #ccc;"><strong>{{total_negocio}}</strong></td></tr>
    <tr><td style="padding:4px 8px;text-align:right;">Total Mensal (MRR):</td><td style="padding:4px 8px;text-align:right;"><strong>{{mrr_total}}</strong></td></tr>
    <tr><td style="padding:4px 8px;text-align:right;">Total Vigência do Contrato:</td><td style="padding:4px 8px;text-align:right;"><strong>{{total_vigencia_contrato}}</strong></td></tr>
  </tbody>
</table>`;

/**
 * Snapshot imutável de uma versão do template de contrato
 * Criado automaticamente via trigger a cada UPDATE no contract_templates
 * 
 * RELEASE: v1.0.0 - 2026-02-19
 */
export interface ContractTemplateVersion {
  id: string;
  tenant_id: string;
  template_id: string;
  version_number: number;
  content_html: string;
  variables: ContractVariable[];
  background_image_path: string | null;
  bg_margin_top_mm: number;
  bg_margin_bottom_mm: number;
  show_page_numbers: boolean;
  page_number_position: string;
  pagination_y_offset_mm: number;
  base_pdf_path: string | null;
  cover_pdf_path: string | null;
  base_pdf_position: string;
  change_note: string | null;
  created_by: string | null;
  created_at: string;
}

/**
 * Papel de signatário padrão no published_contract
 */
export interface DefaultSigner {
  sign_order: number;
  signer_role: string;
  delivery_method: 'email' | 'whatsapp' | 'sms';
  /** Nome padrão do signatário (pré-preenchido no wizard) */
  default_name?: string;
  /** Email padrão do signatário (pré-preenchido no wizard) */
  default_email?: string;
  /** Telefone padrão do signatário (pré-preenchido no wizard) */
  default_phone?: string;
}

/**
 * Status possíveis de um contrato publicado
 */
export type PublishedContractStatus = 'draft' | 'production' | 'inactive';

/**
 * Contrato configurado para uso em produção
 * Entidade intermediária entre template e instância de contrato gerada
 * 
 * RELEASE: v1.0.0 - 2026-02-19
 */
export interface PublishedContract {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  template_id: string;
  /** NULL = usa sempre a versão mais recente */
  template_version_id: string | null;
  /** Provider de assinatura digital vinculado */
  signature_config_id: string | null;
  status: PublishedContractStatus;
  allowed_delivery_methods: ('email' | 'whatsapp' | 'sms')[];
  require_selfie: boolean;
  /** IDs dos workspaces vinculados (array vazio = todos) */
  workspace_ids: string[];
  /** Signatários padrão pré-configurados */
  default_signers: DefaultSigner[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Joins opcionais */
  contract_templates?: ContractTemplate;
  contract_template_versions?: ContractTemplateVersion;
  tenant_signature_configs?: {
    id: string;
    is_default: boolean;
    is_active: boolean;
    signature_providers?: { id: string; name: string; display_name: string; slug: string };
  };
}

/**
 * Input para criar/atualizar um contrato publicado
 */
export interface PublishedContractInput {
  name: string;
  description?: string;
  template_id: string;
  template_version_id?: string | null;
  signature_config_id?: string | null;
  status?: PublishedContractStatus;
  allowed_delivery_methods?: ('email' | 'whatsapp' | 'sms')[];
  require_selfie?: boolean;
  workspace_ids?: string[];
  default_signers?: DefaultSigner[];
}

/**
 * Labels visuais para status de contrato publicado
 */
export const PUBLISHED_CONTRACT_STATUS_LABELS: Record<PublishedContractStatus, { label: string; color: string }> = {
  draft: { label: 'Rascunho', color: 'bg-muted text-muted-foreground' },
  production: { label: 'Produção', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
  inactive: { label: 'Inativo', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
};

/**
 * Papéis de signatário disponíveis para configuração
 */
export const SIGNER_ROLES = [
  { value: 'contratado', label: 'Contratado' },
  { value: 'contratante', label: 'Contratante' },
  { value: 'vendedor', label: 'Vendedor' },
  { value: 'representante_legal', label: 'Representante Legal' },
  { value: 'fiador', label: 'Fiador' },
  { value: 'testemunha', label: 'Testemunha' },
] as const;

/**
 * Categorias de contratos disponíveis
 */
export const CONTRACT_CATEGORIES = [
  { value: 'geral', label: 'Geral' },
  { value: 'venda', label: 'Venda' },
  { value: 'servico', label: 'Serviço' },
  { value: 'parceria', label: 'Parceria' },
  { value: 'locacao', label: 'Locação' },
  { value: 'prestacao', label: 'Prestação de Serviço' },
] as const;

/**
 * Labels visuais para status de contrato
 */
/**
 * Labels visuais para status de assinatura individual
 */
export const SIGNATURE_STATUS_LABELS: Record<SignatureStatus, { label: string; color: string }> = {
  pending: { label: 'Pendente', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
  signed: { label: 'Assinado', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
  refused: { label: 'Recusado', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
  expired: { label: 'Expirado', color: 'bg-muted text-muted-foreground' },
};

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, { label: string; color: string }> = {
  draft: { label: 'Rascunho', color: 'bg-muted text-muted-foreground' },
  generated: { label: 'PDF Gerado', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
  sent: { label: 'Enviado', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
  signed: { label: 'Assinado', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
  expired: { label: 'Expirado', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
  cancelled: { label: 'Cancelado', color: 'bg-destructive/10 text-destructive' },
  queued_pdf: { label: 'Gerando PDF...', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
  processing_pdf: { label: 'Gerando PDF...', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
  queued_signature: { label: 'Enviando...', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
  processing_signature: { label: 'Enviando...', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
  error: { label: 'Erro', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
};
